import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { countryName, getCourseCatalog, type CourseCatalog } from '../../services/coursesApi';

type Destination = {
  name: string;
  code: string;
  flagCode: string;
};

const destinations: Destination[] = [
    { name: 'United States', code: 'US', flagCode: 'us' },
    { name: 'Canada', code: 'CA', flagCode: 'ca' },
    { name: 'United Kingdom', code: 'UK', flagCode: 'gb' },
    { name: 'Ireland', code: 'IE', flagCode: 'ie' },
    { name: 'Australia', code: 'AU', flagCode: 'au' },
    { name: 'New Zealand', code: 'NZ', flagCode: 'nz' },
];

const StudyDestinations = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  useEffect(() => {
    getCourseCatalog()
      .then(setCatalog)
      .catch((error: unknown) => console.error('Could not load destination data:', error));
  }, []);

  const getDestinationData = (destination: Destination) => {
    const matchingCountry = catalog?.countries.find((item) =>
      countryName(item.code).toLowerCase() === destination.name.toLowerCase()
      || item.name.toLowerCase() === destination.name.toLowerCase(),
    );
    const code = matchingCountry?.code || destination.code;
    const providers = catalog?.providers.filter((provider) =>
      provider.country_code.toLowerCase() === code.toLowerCase()
      || countryName(provider.country_code).toLowerCase() === destination.name.toLowerCase(),
    ) || [];

    return {
      code,
      providerCount: providers.length,
      courseCount: providers.reduce((total, provider) => total + (provider.course_count || 0), 0),
    };
  };

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <motion.div
        ref={ref}
        className="max-w-7xl mx-auto px-6"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
      >
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6">Choose Your Study Destination</h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Explore universities and courses from our six study destinations. Select a country to view the institutions and programmes available in our live database.
          </p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((destination) => {
            const data = getDestinationData(destination);
            return (
              <motion.div key={destination.code} variants={cardVariants} whileHover={{ y: -8 }}>
                <Link
                  to={`/courses?country=${encodeURIComponent(data.code)}`}
                  className="block h-full bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="flex min-h-48 flex-col items-center justify-center gap-5 bg-white p-8">
                    <div className="flex items-center justify-between">
                      <img src={`https://flagcdn.com/w160/${destination.flagCode}.png`} alt={`${destination.name} flag`} className="h-28 w-44 rounded-lg object-cover shadow-md" loading="lazy" />
                    </div>
                    <h3 className="text-center text-2xl font-bold text-gray-800">{destination.name}</h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold text-gray-800">{catalog ? data.providerCount.toLocaleString() : '—'}</p>
                        <p className="text-sm text-gray-500">Universities</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{catalog ? data.courseCount.toLocaleString() : '—'}</p>
                        <p className="text-sm text-gray-500">Courses</p>
                      </div>
                    </div>
                    <p className="mt-5 inline-flex items-center gap-2 font-bold text-blue-600">
                      Explore universities and courses
                    </p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="text-center">
          <Link to="/courses" className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full text-lg transition-all duration-300">
            <i className="ri-book-line text-xl" />View All Courses
          </Link>
        </div>
      </motion.div>
    </section>
  );
};

export default StudyDestinations;